/**
 * AmbientParticleManager — level-wide atmospheric particle emitters.
 *
 * Creates continuous ambient particle systems tailored to each level's mood:
 *   - LiminalAtrium: Warm golden dust motes floating gently in sunbeams.
 *   - RedwoodScrapyard: Copper steam wisps & sparks drifting through redwoods.
 *   - OvergrownConservatory: Bioluminescent emerald/cyan spores floating through flora.
 *   - ClockworkCanopy: Starry astronomical sparkles drifting through observatory glass.
 *
 * Design & Performance:
 *   - Uses a shared soft-radial DynamicTexture sprite.
 *   - Listens to ACCESSIBILITY_CHANGED to scale emitRate for reducedMotion.
 *   - Integrates with camera Z distance for culling during wide co-op zoom-outs.
 *   - Disposes all particle resources cleanly on level teardown.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { isLowFiMode } from "../core/lowFiMode";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { AccessibilitySystem } from "./AccessibilitySystem";
import { particleDensityFactor } from "./particleScaling";

/** Create a shared soft radial gradient sprite for particle rendering. */
function makeAmbientParticleSprite(scene: Scene): Texture {
  const size = 64;
  const dt = new DynamicTexture("ambientParticleSprite", size, scene, false);
  const ctx = dt.getContext();
  const g = ctx.createRadialGradient(size / 2, size / 2, 1, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.4, "rgba(255,255,255,0.6)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  dt.update(false);
  dt.hasAlpha = true;
  return dt;
}

export interface AmbientParticleConfig {
  baseEmitRate: number;
  minSize: number;
  maxSize: number;
  minLifeTime: number;
  maxLifeTime: number;
  minEmitPower: number;
  maxEmitPower: number;
  color1: Color4;
  color2: Color4;
  colorDead: Color4;
  gravity: Vector3;
  direction1: Vector3;
  direction2: Vector3;
  emitterBox: { min: Vector3; max: Vector3 };
}

const LEVEL_PARTICLE_CONFIGS: Record<string, AmbientParticleConfig> = {
  LiminalAtrium: {
    baseEmitRate: 8,
    minSize: 0.02,
    maxSize: 0.06,
    minLifeTime: 3.0,
    maxLifeTime: 6.0,
    minEmitPower: 0.1,
    maxEmitPower: 0.3,
    color1: new Color4(1.0, 0.88, 0.55, 0.2),    // Warm golden dust — subtle
    color2: new Color4(1.0, 0.95, 0.7, 0.12),
    colorDead: new Color4(0.8, 0.7, 0.4, 0.0),
    gravity: new Vector3(0, 0.02, 0),
    direction1: new Vector3(-0.2, 0.2, -0.1),
    direction2: new Vector3(0.2, 0.5, 0.1),
    emitterBox: { min: new Vector3(-8, 0, -2), max: new Vector3(8, 5, 2) },
  },
  RedwoodScrapyard: {
    baseEmitRate: 12,
    minSize: 0.03,
    maxSize: 0.08,
    minLifeTime: 2.5,
    maxLifeTime: 5.0,
    minEmitPower: 0.2,
    maxEmitPower: 0.6,
    color1: new Color4(0.9, 0.55, 0.3, 0.2),     // Copper steam — subtle
    color2: new Color4(0.7, 0.4, 0.2, 0.12),
    colorDead: new Color4(0.3, 0.15, 0.05, 0.0),
    gravity: new Vector3(0, 0.05, 0),
    direction1: new Vector3(-0.4, 0.3, -0.2),
    direction2: new Vector3(0.4, 0.8, 0.2),
    emitterBox: { min: new Vector3(-20, 0, -3), max: new Vector3(120, 10, 3) },
  },
  OvergrownConservatory: {
    baseEmitRate: 15,
    minSize: 0.03,
    maxSize: 0.09,
    minLifeTime: 3.5,
    maxLifeTime: 7.0,
    minEmitPower: 0.15,
    maxEmitPower: 0.45,
    color1: new Color4(0.0, 1.0, 0.53, 0.2),    // Bioluminescent spores — subtle
    color2: new Color4(0.0, 0.8, 0.9, 0.12),
    colorDead: new Color4(0.0, 0.3, 0.2, 0.0),
    gravity: new Vector3(0, 0.01, 0),
    direction1: new Vector3(-0.3, 0.1, -0.2),
    direction2: new Vector3(0.3, 0.4, 0.2),
    emitterBox: { min: new Vector3(-10, 0, -3), max: new Vector3(140, 12, 3) },
  },
  ClockworkCanopy: {
    baseEmitRate: 14,
    minSize: 0.02,
    maxSize: 0.07,
    minLifeTime: 3.0,
    maxLifeTime: 6.0,
    minEmitPower: 0.1,
    maxEmitPower: 0.4,
    color1: new Color4(0.9, 0.8, 0.4, 0.2),     // Starry sparkles — subtle
    color2: new Color4(0.6, 0.7, 1.0, 0.12),
    colorDead: new Color4(0.2, 0.2, 0.4, 0.0),
    gravity: new Vector3(0, -0.01, 0),
    direction1: new Vector3(-0.2, -0.1, -0.2),
    direction2: new Vector3(0.2, 0.3, 0.2),
    emitterBox: { min: new Vector3(-10, 0, -3), max: new Vector3(120, 15, 3) },
  },
};

export class AmbientParticleManager {
  private scene: Scene;
  private particleSystem: ParticleSystem | null = null;
  private sprite: Texture | null = null;
  private baseEmitRate = 0;
  private unsubs: Array<() => void> = [];
  private isCulled = false;

  constructor(scene: Scene, levelKey: string) {
    this.scene = scene;
    if (isLowFiMode()) {
      return; // Low-fi mode suppresses ambient particles for performance.
    }
    const config = LEVEL_PARTICLE_CONFIGS[levelKey];
    if (!config) return;

    this.sprite = makeAmbientParticleSprite(scene);
    const ps = new ParticleSystem(`ambient_${levelKey}`, 300, scene);
    ps.particleTexture = this.sprite;

    ps.minEmitBox = config.emitterBox.min;
    ps.maxEmitBox = config.emitterBox.max;

    ps.color1 = config.color1;
    ps.color2 = config.color2;
    ps.colorDead = config.colorDead;

    ps.minSize = config.minSize;
    ps.maxSize = config.maxSize;
    ps.minLifeTime = config.minLifeTime;
    ps.maxLifeTime = config.maxLifeTime;
    ps.minEmitPower = config.minEmitPower;
    ps.maxEmitPower = config.maxEmitPower;
    ps.gravity = config.gravity;
    ps.direction1 = config.direction1;
    ps.direction2 = config.direction2;

    this.baseEmitRate = config.baseEmitRate;
    this.updateEmitRate();
    ps.start();
    this.particleSystem = ps;

    // Listen to accessibility setting updates
    this.unsubs.push(
      gameEvents.on(GameEvents.ACCESSIBILITY_CHANGED, () => {
        this.updateEmitRate();
      })
    );
  }

  private updateEmitRate(): void {
    if (!this.particleSystem || this.isCulled) return;
    const a11y = AccessibilitySystem.getInstance().state;
    const factor = particleDensityFactor(a11y.reducedMotion);
    this.particleSystem.emitRate = Math.max(1, Math.round(this.baseEmitRate * factor));
  }

  /** Temporarily cull or restore particle emission during extreme camera zoom-out. */
  setCulled(culled: boolean): void {
    if (!this.particleSystem || this.isCulled === culled) return;
    this.isCulled = culled;
    if (culled) {
      this.particleSystem.emitRate = 0;
    } else {
      this.updateEmitRate();
    }
  }

  /** Dispose particle resources and unsubscribe event listeners. */
  dispose(): void {
    for (const fn of this.unsubs) fn();
    this.unsubs = [];
    if (this.particleSystem) {
      this.particleSystem.stop();
      this.particleSystem.dispose();
      this.particleSystem = null;
    }
    if (this.sprite) {
      this.sprite.dispose();
      this.sprite = null;
    }
  }
}
